import { lazy, Suspense, type ComponentType } from 'react';
import { Navigate, useLocation, type RouteObject } from 'react-router-dom';

import { AuthLayout } from '@/components/layout/AuthLayout';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { ProjectLayout } from '@/components/layout/ProjectLayout';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { ProtectedRoute } from '@/components/guards/ProtectedRoute';
import { AdminRoute } from '@/components/guards/AdminRoute';
import { Skeleton } from '@/components/ui/skeleton';
import { RouteErrorBoundary } from '@/components/common/RouteErrorBoundary';

/** All page modules are dynamically imported so each route ships as its own
 *  chunk and the initial bundle stays slim (only the shell + layouts). */
type AnyComponent = ComponentType<Record<string, never>>;

function lazyPage(
  importer: () => Promise<Record<string, AnyComponent>>,
  name: string,
) {
  const Component = lazy(async () => {
    const mod = await importer();
    return { default: mod[name] };
  });
  return (
    <Suspense
      fallback={
        <div className="space-y-3">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      }
    >
      <Component />
    </Suspense>
  );
}

/** `<Navigate>` that keeps the query string and hash, so old links that
 *  carried parameters still land with them on the new path. */
function RedirectPreservingSearch({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

export const routes: RouteObject[] = [
  { path: '/', element: <Navigate to="/login" replace /> },
  {
    element: <AuthLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: '/login', element: lazyPage(() => import('@/features/auth/LoginPage'), 'LoginPage') },
      {
        path: '/reset-password',
        element: lazyPage(
          () => import('@/features/auth/ResetPasswordPage'),
          'ResetPasswordPage',
        ),
      },
      {
        path: '/verify-email',
        element: lazyPage(() => import('@/features/auth/VerifyEmailPage'), 'VerifyEmailPage'),
      },
      {
        // Buy links from the web-site's pricing page. Outside ProtectedRoute:
        // a signed-out visitor must see what they are buying and have the
        // purchase kept through sign-in, verification and approval.
        path: '/buy/*',
        element: lazyPage(() => import('@/features/billing/BuyPage'), 'BuyPage'),
      },
      {
        // Stripe Checkout returns here, signed in or not.
        path: '/billing/checkout/success',
        element: lazyPage(
          () => import('@/features/billing/CheckoutResultPages'),
          'CheckoutSuccessPage',
        ),
      },
      {
        path: '/billing/checkout/cancel',
        element: lazyPage(
          () => import('@/features/billing/CheckoutResultPages'),
          'CheckoutCancelPage',
        ),
      },
    ],
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="projects" replace /> },
      {
        path: 'projects',
        element: lazyPage(() => import('@/features/projects/ProjectsPage'), 'ProjectsPage'),
      },
      // Start page: one choice between Deploy, Size and Fit. Deploy and Size
      // are wizard flows; Fit is the GPU explorer.
      // One optional-segment route, so moving between the landing and a line
      // keeps the same WizardPage instance instead of remounting it.
      {
        path: 'start/:line?',
        element: lazyPage(() => import('@/features/projects/ProjectsV2Page'), 'ProjectsV2Page'),
      },
      { path: 'start/fit', element: <RedirectPreservingSearch to="/dashboard/gpu-explorer" /> },
      { path: 'projects-v2', element: <RedirectPreservingSearch to="/dashboard/start" /> },
      {
        path: 'projects/new',
        element: lazyPage(
          () => import('@/features/projects/ProjectCreatorPage'),
          'ProjectCreatorPage',
        ),
      },
      {
        path: 'model-advisor',
        element: lazyPage(
          () => import('@/features/model-advisor/ModelAdvisorPage'),
          'ModelAdvisorPage',
        ),
      },
      {
        path: 'gpu-explorer',
        element: lazyPage(
          () => import('@/features/gpu-explorer/GpuExplorerPage'),
          'GpuExplorerPage',
        ),
      },
      {
        path: 'projects/:projectId',
        element: <ProjectLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { index: true, element: <Navigate to="deployments" replace /> },
          {
            path: 'config',
            element: lazyPage(
              () => import('@/features/projects/ProjectConfigPage'),
              'ProjectConfigPage',
            ),
          },
          {
            path: 'recommendations',
            element: lazyPage(
              () => import('@/features/recommendations/RecommendationsRouter'),
              'RecommendationsRouter',
            ),
          },
          {
            path: 'recommendations/assisted',
            element: lazyPage(
              () => import('@/features/recommendations/AssistedRecommendationsPage'),
              'AssistedRecommendationsPage',
            ),
          },
          {
            path: 'deployments',
            element: lazyPage(
              () => import('@/features/deployments/ProjectDeploymentsPage'),
              'ProjectDeploymentsPage',
            ),
          },
          {
            path: 'dashboard',
            element: lazyPage(
              () => import('@/features/deployments/ProjectDashboardPage'),
              'ProjectDashboardPage',
            ),
          },
          {
            path: 'deploy',
            element: lazyPage(
              () => import('@/features/deployments/DeploymentPage'),
              'DeploymentPage',
            ),
          },
          {
            path: 'recommendations/:recommendationId/deploy',
            element: lazyPage(
              () => import('@/features/deployments/DeploymentPage'),
              'DeploymentPage',
            ),
          },
        ],
      },
      {
        // Final step of the assisted-create wizard / IaC export. Mounted at
        // the dashboard level so the project's Configuration/Recommendations/
        // Deployments tabs don't surround it — matches the legacy mount and
        // gives the wizard handoff a clean full-page UX.
        path: 'projects/:projectId/recommendations/:recommendationId/deploy-opentofu',
        element: lazyPage(
          () => import('@/features/deployments/OpenTofuDeploymentPage'),
          'OpenTofuDeploymentPage',
        ),
      },
      {
        // Place Managed: the launch confirmation and the deployment's own
        // page. Dashboard-level, like the OpenTofu page, so the project tabs
        // do not surround them.
        path: 'projects/:projectId/recommendations/:recommendationId/deploy-managed',
        element: lazyPage(
          () => import('@/features/managed/ManagedLaunchPage'),
          'ManagedLaunchPage',
        ),
      },
      {
        path: 'projects/:projectId/managed-deployments/:deploymentId',
        element: lazyPage(
          () => import('@/features/managed/ManagedDeploymentPage'),
          'ManagedDeploymentPage',
        ),
      },
      {
        path: 'deployments',
        element: lazyPage(
          () => import('@/features/deployments/DeploymentsPage'),
          'DeploymentsPage',
        ),
      },
      {
        path: 'deployments/:deploymentId',
        element: lazyPage(
          () => import('@/features/deployments/DeploymentDashboardPage'),
          'DeploymentDashboardPage',
        ),
      },
      {
        path: 'models',
        element: lazyPage(() => import('@/features/models/ModelsPage'), 'ModelsPage'),
      },
      {
        // Same component as /models — ModelsPage auto-opens the upload modal
        // when it sees this URL. Keeps the upload deep-linkable.
        path: 'models/upload',
        element: lazyPage(() => import('@/features/models/ModelsPage'), 'ModelsPage'),
      },
      {
        // Splat route — model_identity contains slashes (e.g.
        // meta-llama/Llama-3.1-8B-Instruct), which a plain `:identity` param
        // would only capture up to the first `/`. ModelDetailPage reads the
        // full identity via useParams()['*'].
        path: 'models/detail/*',
        element: lazyPage(
          () => import('@/features/models/ModelDetailPage'),
          'ModelDetailPage',
        ),
      },
      {
        path: 'models/compare',
        element: lazyPage(
          () => import('@/features/models/ModelComparePage'),
          'ModelComparePage',
        ),
      },
      {
        // The seat e-mails link here (auth's BILLING_PAGE_PATH): keep the path.
        path: 'billing',
        element: lazyPage(() => import('@/features/billing/BillingPage'), 'BillingPage'),
      },
      {
        // Where a seat invite link lands; the token is in the fragment.
        path: 'billing/seat-invite',
        element: lazyPage(() => import('@/features/billing/SeatInvitePage'), 'SeatInvitePage'),
      },
      {
        path: 'settings',
        element: lazyPage(() => import('@/features/settings/SettingsPage'), 'SettingsPage'),
      },
      {
        path: 'admin',
        element: (
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          { index: true, element: <Navigate to="users" replace /> },
          {
            path: 'users',
            element: lazyPage(() => import('@/features/admin/UserAdminPage'), 'UserAdminPage'),
          },
          {
            path: 'models',
            element: lazyPage(() => import('@/features/admin/ModelAdminPage'), 'ModelAdminPage'),
          },
          {
            path: 'devices',
            element: lazyPage(
              () => import('@/features/admin/DeviceAdminPage'),
              'DeviceAdminPage',
            ),
          },
          {
            path: 'instances',
            element: lazyPage(
              () => import('@/features/admin/InstanceAdminPage'),
              'InstanceAdminPage',
            ),
          },
          {
            path: 'opentofu-cache',
            element: lazyPage(
              () => import('@/features/admin/OpenTofuCachePage'),
              'OpenTofuCachePage',
            ),
          },
          {
            path: 'billing',
            element: lazyPage(
              () => import('@/features/admin/billing/ClientBillingPage'),
              'ClientBillingPage',
            ),
          },
          {
            path: 'billing/listed',
            element: lazyPage(
              () => import('@/features/admin/billing/ListedItemsPage'),
              'ListedItemsPage',
            ),
          },
          { path: 'pricing', element: <Navigate to="../instances" replace /> },
        ],
      },
      {
        path: 'feature-unavailable',
        element: lazyPage(
          () => import('@/features/FeatureUnavailablePage'),
          'FeatureUnavailablePage',
        ),
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
];
